import { analyze } from './analyze';
import { CATALOG, catOf, type Part, type PartLookup } from './catalog';
import { onSeg, pinsW } from './geometry';
import { autoLayout, blankLayout, busbarDims, busbarSize, busFed, depthForDevices, feet, FOOT, footed, layoutDevices, phaseFeet, sectionLayout, type AutoLayoutResult, type SectionLayoutOptions } from './layout';
import { LIB } from './library';
import { pickPart, ratedOf, type GenResult, type PartWant } from './generate';
import { t as $t, tk } from './i18n';
import { newId } from './tagging';
import type { LayoutBar, Page, PanelLayout, Project, Pt, PType, Sym, SymProps } from './types';

/*
 * Distribution board generation: an incomer (main switch, current transformers
 * with an energy analyser, surge protection) and outgoing feeders (breaker,
 * optional residual current device, terminals, cable) from a feeder list.
 *
 * Sheet grid as in the motor generator: one current path per 40 mm column.
 */

export type IncomerKind = 'lbs' | 'mccb' | 'acb';
export interface IncomerSpec {
  kind: IncomerKind;
  /** rated current of the main switch in A */
  amps: number;
  /** surge protective device after the main switch */
  spd: boolean;
  /** current transformers and an energy analyser */
  meter: boolean;
  /** automatic transfer to a standby generator: grid and generator contactors, interlocked */
  ats?: boolean;
  /** with ats: transfer by hand on an I-0-II change-over switch (the board's main switch), no contactors */
  manual?: boolean;
  /** power factor correction: capacitor steps in kvar (empty = none) */
  comp?: number[];
}

/** Capacitor steps from text like "5, 10, 2×20" (kvar, 1–100 each, at most 9 steps). */
export function parseSteps(text: string): { steps: number[]; error: string } {
  const steps: number[] = [];
  for (const part of text.split(/[;\s]+|,(?!\d(?:\D|$))/).filter(Boolean)) {
    const m = /^(?:(\d+)\s*[x×*]\s*)?(\d+(?:[.,]\d+)?)(?:kvar)?$/i.exec(part);
    if (!m) return { steps: [], error: $t('Kademe okunamadı: "{0}"', part) };
    const k = num(m[2]);
    if (!(k >= 1 && k <= 100)) return { steps: [], error: $t('Kademe 1–100 kvar arasında olmalı: "{0}"', part) };
    for (let i = 0; i < (m[1] ? +m[1] : 1); i++) steps.push(k);
  }
  if (steps.length > 9) return { steps: [], error: $t('En fazla 9 kademe olabilir.') };
  return { steps, error: '' };
}

/**
 * Reads a single-line page drawn with the single-line symbols: the busbar is the longest horizontal
 * line, the branches above it are incomers (two or more = transfer to a generator), the branches
 * below are feeders, left to right. A branch is followed symbol by symbol; its breaker (and RCD)
 * give the current, poles, cable length, tag and description entered on the symbols.
 */
export function boardFromSingleLine(page: Page): { kind: IncomerKind; amps: number; ats: boolean; manual?: boolean; feeders: FeederSpec[]; warnings: string[] } | null {
  const warnings: string[] = [];
  const key = (x: number, y: number) => `${Math.round(x * 10)},${Math.round(y * 10)}`;
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    let r = k;
    while (parent.get(r) !== undefined && parent.get(r) !== r) r = parent.get(r)!;
    parent.set(k, r);
    return r;
  };
  const join = (a: string, b: string) => {
    if (!parent.has(a)) parent.set(a, a);
    if (!parent.has(b)) parent.set(b, b);
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  const devs = page.syms.filter((s) => LIB[s.type]?.role === 'sl' && LIB[s.type].pins.length === 2);
  const segs: [Pt, Pt][] = [];
  for (const w of page.wires) for (let i = 0; i < w.pts.length - 1; i++) segs.push([w.pts[i], w.pts[i + 1]]);
  const points: Pt[] = [...page.wires.flatMap((w) => w.pts), ...devs.flatMap((s) => pinsW(s))];
  for (const w of page.wires) for (let i = 0; i < w.pts.length - 1; i++) join(key(...w.pts[i]), key(...w.pts[i + 1]));
  for (const p of points) {
    if (!parent.has(key(...p))) parent.set(key(...p), key(...p));
    for (const [a, b] of segs) if (onSeg(p[0], p[1], a, b)) join(key(...p), key(...a));
  }
  // busbar: the net with the most horizontal line length
  const len = new Map<string, number>();
  for (const [a, b] of segs) if (Math.abs(a[1] - b[1]) < 1e-6) len.set(find(key(...a)), (len.get(find(key(...a))) ?? 0) + Math.abs(a[0] - b[0]));
  const bus = [...len].sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!bus || !devs.length) return null;
  const ends = (s: Sym) => {
    const [p, q] = pinsW(s);
    const [top, bottom] = p[1] <= q[1] ? [p, q] : [q, p];
    return { top: find(key(...top)), bottom: find(key(...bottom)), x: top[0] };
  };
  const E = new Map(devs.map((s) => [s.id, ends(s)]));
  const amps = (s: Sym | undefined) => Number(String(s?.props.amps ?? '').replace(',', '.'));
  /** a branch from the busbar, symbol by symbol, upwards or downwards */
  const branch = (start: Sym, up: boolean) => {
    const chain = [start], seen = new Set([start.id]);
    for (let cur = start; chain.length < 12; ) {
      const net = up ? E.get(cur.id)!.top : E.get(cur.id)!.bottom;
      const next = devs.find((d) => !seen.has(d.id) && (up ? E.get(d.id)!.bottom : E.get(d.id)!.top) === net);
      if (!next) break;
      chain.push(next);
      seen.add(next.id);
      cur = next;
    }
    return chain;
  };
  /** upwards from the busbar every source is its own chain, also where two join above a common device */
  const sources = (start: Sym, below: Sym[] = []): Sym[][] => {
    const chain = [...below, start];
    for (let cur = start; chain.length < 12; ) {
      const top = E.get(cur.id)!.top;
      const up = devs.filter((d) => !chain.includes(d) && E.get(d.id)!.bottom === top);
      if (up.length > 1) return up.flatMap((d) => sources(d, chain));
      if (!up.length) break;
      chain.push(up[0]);
      cur = up[0];
    }
    return [chain];
  };
  const incomers = devs.filter((d) => E.get(d.id)!.bottom === bus).flatMap((d) => sources(d));
  const mains = incomers.map((c) => c.find((d) => d.type === 'sl_cb')).filter((d): d is Sym => !!d);
  if (!mains.length) warnings.push($t('Tek hatta baranın üstünde bir ana şalter bulunamadı; ana şalter akımını elle girin.'));
  const main = mains.sort((a, b) => amps(b) - amps(a))[0];
  const mainAmps = amps(main);
  if (main && !(mainAmps > 0)) warnings.push($t('{0}: ana şalterin akımı girilmemiş.', main.tag || $t('Ana şalter')));
  const feeders: FeederSpec[] = [];
  devs
    .filter((d) => E.get(d.id)!.top === bus)
    .sort((a, b) => E.get(a.id)!.x - E.get(b.id)!.x)
    .forEach((head) => {
      const chain = branch(head, false);
      const brk = chain.find((d) => d.type === 'sl_cb' || d.type === 'sl_fsd'), rcd = chain.find((d) => d.type === 'sl_rcd');
      const fuse = brk?.type === 'sl_fsd', meter = chain.some((d) => d.type === 'sl_kwh');
      const who = brk?.tag || head.tag || $t('Çıkış');
      let a = amps(brk) || amps(rcd);
      if (!(a > 0)) {
        warnings.push($t('{0}: akım girilmemiş, 16 A alındı.', who));
        a = 16;
      }
      const poles: 1 | 3 = brk?.props.poles === '1' && !fuse ? 1 : 3;
      if (poles === 1 && a > MCB_MAX) {
        warnings.push($t('{0}: tek fazlı çıkış en fazla {1} A olabilir; 3 fazlı alındı.', who, MCB_MAX));
      }
      const cableLen = Number(String(brk?.props.length ?? '').replace(',', '.'));
      feeders.push({
        tag: brk?.tag ?? '',
        desc: brk?.desc || rcd?.desc || '',
        amps: Math.min(a, MAX_FEEDER),
        poles: poles === 1 && a > MCB_MAX ? 3 : poles,
        rcd: !!rcd,
        ...(cableLen > 0 ? { cableLen } : {}),
        ...(fuse ? { fuse } : {}),
        ...(meter ? { meter } : {}),
      });
    });
  if (!feeders.length) warnings.push($t('Tek hatta baradan inen bir çıkış bulunamadı.'));
  const sk = main?.props.swkind;
  const kind: IncomerKind = sk === 'lbs' || sk === 'acb' || sk === 'mccb' ? sk : mainAmps > 630 ? 'acb' : 'mccb';
  // a change-over switch: the same switch-disconnector tag in both sources
  const cos = incomers.map((c) => c.find((d) => d.type === 'sl_lbs' || d.type === 'sl_ds')?.tag).filter(Boolean);
  const manual = incomers.length >= 2 && cos.length >= 2 && cos[0] === cos[1];
  return { kind, amps: mainAmps > 0 ? mainAmps : 250, ats: incomers.length >= 2, ...(manual ? { manual } : {}), feeders, warnings };
}

/** Largest main switch: air circuit breakers of frame 3 go to 6300 A. */
export const MAX_INCOMER = 6300;
/** From this rated current the main circuit is copper busbar, not cable. */
export const BUSBAR_FROM = 400;
/** Mounting plate for a board of this rating (grows when the devices do not fit). */
export function boardPlate(amps: number): [number, number] {
  return amps <= 125 ? [600, 800] : amps <= 400 ? [800, 1200] : amps <= 1000 ? [1000, 1800] : [1200, 2000];
}

/**
 * Adds a mounting plate page after the board pages and places every device of the project that is
 * not on a plate yet in rows on DIN rails with ducts between; the 3D view works from this page.
 */
export function addBoardLayout(pr: Project, amps: number, parts: PartLookup = catOf, groups?: SectionLayoutOptions['groups'], o: { nHalf?: boolean } = {}): { page: Page; result: AutoLayoutResult } {
  const [w, h] = boardPlate(amps);
  const layout = blankLayout(w, h);
  // air circuit breakers and big moulded case breakers need a deeper cabinet
  if (amps > 400) layout.depth = amps > 1000 ? 600 : 400;
  const page: Page = { id: newId(), desc: $t('Pano yerleşimi'), kind: 'layout', layout, syms: [], wires: [] };
  pr.pages.push(page);
  const pi = pr.pages.length - 1;
  // and never shallower than its deepest device (an air circuit breaker chosen for a small board)
  const deep = () => (layout.depth = Math.max(layout.depth ?? 0, depthForDevices(layout, layoutDevices(analyze(pr), parts))));
  if (amps < BUSBAR_FROM) {
    const result = autoLayout(pr, analyze(pr), pi, { mode: 'fresh', growPlate: true }, parts);
    deep();
    return { page, result };
  }
  // floor-standing board in compartments by device size and weight: the phase busbars L1 L2 L3 across all
  // compartments in the middle of the board at the same height (one behind the other), N and PE along the bottom
  layout.h = 2000;
  const spec = busbarSize(amps), bw = busbarDims(spec).w, mid = 900;
  const nY = layout.h - 110, peY = layout.h - 60;
  const result = sectionLayout(pr, analyze(pr), pi, { band: [mid - bw / 2 - 30, mid + bw / 2 + 30], bottom: layout.h - 150, groups }, parts);
  // the neutral 1:1 with the phases or, chosen, of half their cross-section
  const nSpec = o.nHalf ? busbarSize(amps / 2) : spec;
  const bar = (x0: number, x1: number): LayoutBar => ({ x: x0 + 30, y: mid, w: x1 - x0 - 60, spec, pe: busbarSize(amps / 2), ...(o.nHalf ? { n: nSpec } : {}), mid: { nY, peY } });
  const spans = result.spans ?? [];
  if (spans.some((s) => s.bus > 0)) {
    // one busbar per section; a coupler compartment belongs to the section on its left, the next section's
    // busbar starts behind the wall (the coupler joins them)
    const ends = new Map<number, [number, number]>();
    let last = 0;
    for (const s of spans) {
      const b = s.bus >= 0 ? s.bus : last;
      const e = ends.get(b);
      ends.set(b, [e ? e[0] : s.x, s.x + s.w]);
      if (s.bus >= 0) last = s.bus;
    }
    layout.bars = [...ends.keys()].sort((a, b) => a - b).map((b) => bar(...ends.get(b)!));
    couplerLinks(pr, layout, spans, mid, spec, nSpec, parts);
  } else layout.bars = [bar(0, layout.w)];
  neutralLinks(pr, layout, mid, nSpec, parts);
  deep();
  return { page, result };
}

/**
 * A bus coupler joins both busbars: its top feet reach the busbar of the section on its left (which ends just past
 * the coupler), its bottom feet a connection bar that runs down, across to the right of the compartment and up to the
 * next section's busbar (which starts there).
 */
function couplerLinks(pr: Project, l: PanelLayout, spans: NonNullable<AutoLayoutResult['spans']>, mid: number, spec: string, nSpec: string, parts: PartLookup) {
  const A = analyze(pr), devs = layoutDevices(A, parts), bw = busbarDims(spec).w;
  spans.forEach((sp, i) => {
    if (sp.bus >= 0) return;
    const left = spans.slice(0, i).reverse().find((s) => s.bus >= 0)?.bus, right = spans.slice(i + 1).find((s) => s.bus >= 0)?.bus;
    const barL = left != null ? l.bars?.[left] : undefined, barR = right != null ? l.bars?.[right] : undefined;
    // the coupler: the biggest breaker on feet in the compartment, under the busbars
    const it = l.items
      .filter((x) => x.x >= sp.x && x.x < sp.x + sp.w)
      .map((x) => ({ x, d: devs.get(x.dev)! }))
      .filter(({ x, d }) => d && footed(A, d) && x.y > mid)
      .sort((a, b) => b.d.w * b.d.h - a.d.w * a.d.h)[0];
    if (!it || !barL || !barR) return;
    const right0 = it.x.x + it.d.w, riser = Math.round((right0 + sp.x + sp.w - 60) / 2);
    if (riser - bw / 2 < right0 + 20) return;
    // the left busbar ends at the coupler's side (its feet are within), the right one starts at the riser
    barL.w = Math.round(right0 - barL.x);
    const end = barR.x + barR.w;
    barR.x = Math.round(riser - bw / 2);
    barR.w = end - barR.x;
    // from each phase foot down to a run under the coupler, across to the riser and up to the busbar
    const foot = it.x.y + it.d.h + FOOT, low = Math.round(foot + 60);
    // each phase from its own foot: down, across and up at its own depth (one behind the other, as the busbars)
    const fx = phaseFeet(it.x.x, it.d).map(Math.round);
    l.links ??= [];
    fx.forEach((x, ph) => l.links!.push({ pts: [[x, foot], [x, low], [riser, low], [riser, mid]], spec, ph }));
    // the protective earth runs through; the neutral too, unless the coupler switches it (4 poles): then busbar A's
    // N ends left of the coupler and rises to its top N foot, its bottom N foot drops to busbar B's N
    const nY = barL.mid!.nY;
    barL.mid!.peSpan = [barL.x, barR.x];
    if (it.d.poles === 4) {
      const xn = Math.round(feet(it.x.x, it.d)[0].x), side = Math.round(it.x.x - 30), over = it.x.y - FOOT - 15;
      barL.mid!.nSpan = [barL.x, side];
      barR.mid!.nSpan = [xn, barR.x + barR.w];
      l.links.push({ pts: [[xn, it.x.y - FOOT], [xn, over], [side, over], [side, nY]], spec: nSpec, kind: 'N' });
      l.links.push({ pts: [[xn, foot], [xn, nY]], spec: nSpec, kind: 'N' });
    } else barL.mid!.nSpan = [barL.x, barR.x];
  });
}

/**
 * The neutral of every 4 pole breaker on the busbar circuit: from its N foot on the busbar side (top under the
 * phase bars, bottom over them) a short way out, then beside the breaker down to the N busbar.
 */
function neutralLinks(pr: Project, l: PanelLayout, mid: number, nSpec: string, parts: PartLookup) {
  const A = analyze(pr), devs = layoutDevices(A, parts), fed = busFed(pr, A);
  const taken = new Set((l.links ?? []).filter((k) => k.kind === 'N').map((k) => `${k.pts[0][0]},${k.pts[0][1]}`));
  const sides = new Map<number, number>(); // down conductors already at this x: the next one steps left
  for (const it of l.items) {
    const d = devs.get(it.dev);
    if (!d || d.poles !== 4 || !footed(A, d) || !fed(it.dev)) continue;
    const bar = l.bars?.find((b) => b.mid && it.x + d.w / 2 >= b.x && it.x + d.w / 2 <= b.x + b.w) ?? l.bars?.find((b) => b.mid);
    if (!bar?.mid) continue;
    const under = it.y > mid, xn = Math.round(feet(it.x, d)[0].x);
    const y0 = under ? it.y - FOOT : it.y + d.h + FOOT;
    if (taken.has(`${xn},${y0}`)) continue;
    let side = Math.round(it.x - 30);
    while (sides.has(side)) side -= 20;
    sides.set(side, 1);
    const out = under ? y0 - 15 : y0 + 15;
    (l.links ??= []).push({ pts: [[xn, y0], [xn, out], [side, out], [side, bar.mid.nY]], spec: nSpec, kind: 'N' });
  }
}

/** Rated current of a three-phase capacitor at 400 V. */
export const capCurrent = (kvar: number) => (kvar * 1000) / (Math.sqrt(3) * 400);
export interface FeederSpec {
  /** breaker tag; empty = next free F (miniature breakers) or Q (moulded case) */
  tag?: string;
  desc: string;
  /** rated current of the feeder breaker in A */
  amps: number;
  poles: 1 | 3;
  /** residual current device after the breaker */
  rcd: boolean;
  /** cable length in metres */
  cableLen?: number;
  /** NH fuse switch-disconnector instead of a breaker (three-phase feeders) */
  fuse?: boolean;
  /** direct connected energy meter (up to METER_MAX) */
  meter?: boolean;
}
/** Largest feeder with a direct connected meter; above it the meter is CT operated (drawn by hand). */
export const METER_MAX = 80;
/** "NH", "sigortalı", "fuse" …, not a miniature circuit breaker ("otomatik sigorta") */
const isFuse = (v: string) => /^(nh|sigortal|bıçak|bicak|fuse|sicherung|fusible|fusível|предохран|保险|熔断|ヒューズ)/i.test(v.trim()) && !/otomatik|breaker|mcb/i.test(v);

export const INCOMER_NAMES: Record<IncomerKind, string> = {
  lbs: tk('Yük ayırıcı'),
  mccb: tk('Kompakt şalter (MCCB)'),
  acb: tk('Açık tip şalter (ACB)'),
};
const INCOMER_TYPE: Record<IncomerKind, string> = { lbs: 'lbs3', mccb: 'mccb3', acb: 'cb3m' };

/** Largest moulded case breaker of the built-in series. */
const MCCB_MAX = 1600;
/** Smallest air circuit breaker frame. */
const ACB_MIN = 630;
/** Largest feeder: moulded case breakers of the built-in series go to 630 A. */
export const MAX_FEEDER = 630;
/** Feeders above this current are connected on the breaker terminals, without rail terminals. */
const DIRECT_FROM = 160;
/** Miniature breakers up to 63 A, moulded case above. */
const MCB_MAX = 63;

const X = (k: number) => 32 + 40 * k;
const COLS = 9;
const num = (s: string) => parseFloat(s.replace(',', '.'));

/** Copper cross-section (mm²) for a breaker rating, installation method C, 30 °C; two cables in parallel above 240 mm². */
export function feederSection(amps: number): string {
  const t: [number, string][] = [[15, '1.5'], [21, '2.5'], [28, '4'], [36, '6'], [50, '10'], [68, '16'], [89, '25'], [110, '35'], [134, '50'], [171, '70'], [207, '95'], [239, '120'], [272, '150'], [310, '185'], [364, '240']];
  const one = t.find(([a]) => amps <= a);
  if (one) return one[1];
  return '2×' + (t.find(([a]) => amps / 2 <= a) ?? [0, '240'])[1];
}

const YES = /^(evet|e|var|yes|y|ja|oui|sí|si|sim|да|是|はい|نعم|हाँ|x|1|30|300|30\s*ma|300\s*ma|rcd|kaçak|fi)$/i;

/**
 * Feeder list pasted from Excel (tab separated) or CSV: tag, description, current (A),
 * poles (1P / 3P), residual current device (yes / no), cable length (m). A header row is skipped.
 */
export function parseFeederList(text: string): { feeders: FeederSpec[]; errors: string[] } {
  const feeders: FeederSpec[] = [], errors: string[] = [];
  const lines = text.split(/\r?\n/).map((l) => l.replace(/^ +| +$/g, '')).filter((l) => l.trim());
  lines.forEach((line, i) => {
    const c = line.split(line.includes('\t') ? '\t' : /[;,](?=(?:[^"]*"[^"]*")*[^"]*$)/).map((x) => x.trim().replace(/^"|"$/g, ''));
    const amps = num((c[2] ?? '').replace(/\s*A$/i, ''));
    if (!Number.isFinite(amps)) {
      if (i === 0) return; // header
      return void errors.push($t('{0}. satır: akım (A) okunamadı', i + 1));
    }
    if (amps <= 0 || amps > MAX_FEEDER) return void errors.push($t('{0}. satır: akım 1–{1} A arasında olmalı', i + 1, MAX_FEEDER));
    const poles: 1 | 3 = /^(1|1p|1f|1~|mono|tek|single|230)/i.test((c[3] ?? '').trim()) ? 1 : 3;
    if (poles === 1 && amps > MCB_MAX) return void errors.push($t('{0}. satır: tek fazlı çıkış en fazla {1} A olabilir', i + 1, MCB_MAX));
    const len = num(c[5] ?? '');
    feeders.push({
      tag: (c[0] ?? '').replace(/^[-=+\s]+/, '').replace(/\s+/g, '').toUpperCase(),
      desc: c[1] ?? '',
      amps,
      poles,
      rcd: YES.test((c[4] ?? '').trim()),
      ...(Number.isFinite(len) && len > 0 ? { cableLen: len } : {}),
      ...(isFuse(c[6] ?? '') ? { fuse: true } : {}),
      ...(YES.test((c[7] ?? '').trim()) ? { meter: true } : {}),
    });
  });
  return { feeders, errors };
}

/** The drawing helpers of a board generator, for what the distribution and power board generators draw alike. */
export interface BoardDraw {
  S: (p: Page, type: string, x: number, y: number, o?: { rot?: number; tag?: string; desc?: string; part?: string; props?: SymProps }) => unknown;
  W: (p: Page, pts: Pt[], cs?: string, col?: string) => unknown;
  tag: (prefix: string) => string;
  has: (pn: string) => string;
  choose: (who: string, what: string, type: string, w: PartWant) => string;
  warnings: string[];
}

/**
 * Earth leakage protection of a three-phase breaker feeder above the RCD range, in the two columns right of its
 * conductors (x … x + 32, N at nX): a core balance CT round the phases and N, the relay supplied from L1 (busbar at
 * y 24) through a fuse that starts below the PE busbar (y 56) and back to the feeder's N, the toroid on S1-S2, and
 * the relay's contact 11-14 driving the breaker's shunt trip coil. Returns the relay's tag.
 */
export function earthLeakage(d: BoardDraw, pg: Page, x: number, nX: number, t: string, amps: number): string {
  const { S, W, tag, has, choose } = d;
  const xr = x + 48, xs = x + 80, FR = tag('F'), FF = tag('F'), TT = tag('T');
  S(pg, 'cbct', x, 108, { tag: TT, desc: $t('{0} toroid', t), part: choose(TT, $t('toroid akım trafosu'), 'cbct', { rated: amps }) });
  W(pg, [[xr, 24], [xr, 60]], '1.5', 'BK');
  S(pg, 'fuse', xr, 60, { tag: FF, part: has('SG-2A-5x20') });
  W(pg, [[xr, 76], [xr, 80]], '1.5', 'BK');
  S(pg, 'efr', xr, 80, { tag: FR, desc: $t('{0} kaçak akım', t), part: has('EFR-230') });
  W(pg, [[x + 36, 104], [xr + 8, 104], [xr + 8, 100]], '0.75', 'BK');
  W(pg, [[x + 36, 112], [xr + 16, 112], [xr + 16, 100]], '0.75', 'BK');
  W(pg, [[xr, 78], [xs, 78], [xs, 84]], '1.5', 'BK');
  S(pg, 'no', xs, 84, { tag: FR, props: { pins: '11-14' } });
  W(pg, [[xs, 100], [xs, 104]], '1.5', 'BK');
  S(pg, 'coil_st', xs, 104, { tag: t, desc: $t('Açtırma bobini'), part: has('AB-230') });
  W(pg, [[xs, 124], [xs, 132], [nX, 132]], '1.5', 'BU');
  W(pg, [[xr, 100], [xr, 132]], '1.5', 'BU');
  d.warnings.push($t('{0}: toroidli kaçak akım koruması bir şablondur: rölenin klemens numaralarını (A1-A2, S1-S2, 11-12/14) ve açma eşiği ile gecikmesini seçilen ürüne göre kontrol edin; kompakt şaltere açtırma bobini eklenmelidir.', t));
  return FR;
}

export function generateDistribution(pr: Project, inc: IncomerSpec, feeders: FeederSpec[], opt: { parts?: Part[]; fallback?: Part[]; singleLine?: boolean } = {}): GenResult {
  const parts = opt.parts ?? CATALOG;
  const warnings: string[] = [];
  // moulded case breakers end at 1600 A: a bigger main switch is an air circuit breaker
  if (inc.kind === 'mccb' && inc.amps > MCCB_MAX) {
    warnings.push($t('Kompakt şalter en fazla {0} A olur; {1} A ana şalter için açık tip şalter (ACB) seçildi.', MCCB_MAX, inc.amps));
    inc = { ...inc, kind: 'acb' };
  }
  // and air circuit breakers start at 630 A: below that the main switch is a moulded case breaker
  if (inc.kind === 'acb' && inc.amps < ACB_MIN) {
    warnings.push($t('Açık tip şalter {0} A altında kullanılmaz; {1} A ana şalter için kompakt şalter seçildi.', ACB_MIN, inc.amps));
    inc = { ...inc, kind: 'mccb' };
  }
  const pages: Page[] = [];
  const has = (pn: string) => (parts.some((p) => p.pn === pn) || opt.fallback?.some((p) => p.pn === pn) ? pn : '');

  // next free number per tag prefix, across the project and everything generated here
  // the breakers, RCDs and contactors of a (hand-drawn) single line only refer to the devices this draws:
  // their tags are free to use; a transformer or motor drawn there is a real device and keeps its tag
  const real = pr.pages.flatMap((p) => p.syms.filter((s) => !['sl_cb', 'sl_rcd', 'sl_k', 'sl_fsd', 'sl_kwh', 'sl_lbs', 'sl_ds'].includes(s.type)));
  const used = new Map<string, number>();
  for (const s of real) {
    const m = /^([A-Z]+)(\d+)$/.exec(s.tag || '');
    if (m) used.set(m[1], Math.max(used.get(m[1]) ?? 0, +m[2]));
  }
  const taken = new Set(real.map((s) => s.tag));
  const tag = (prefix: string) => {
    let n = (used.get(prefix) ?? 0) + 1;
    while (taken.has(prefix + n)) n++;
    used.set(prefix, n);
    taken.add(prefix + n);
    return prefix + n;
  };
  // tags the feeder list gives are reserved before anything is numbered
  const own = feeders.map((f) => {
    const t = f.tag || '';
    if (!t) return '';
    if (taken.has(t)) {
      warnings.push($t('{0} etiketi projede zaten var; yeni etiket verildi', t));
      return '';
    }
    // reserved only: the numbering skips it but does not jump past it (Q9 in the list keeps the main switch Q1)
    taken.add(t);
    return t;
  });
  let term = 0;
  for (const pg of pr.pages) for (const s of pg.syms) if (s.type === 'term' && s.tag === 'X1') term = Math.max(term, parseInt(s.props.pinNo || '0', 10) || 0);

  const page = (desc: string) => {
    const p: Page = { id: newId(), desc, syms: [], wires: [] };
    pages.push(p);
    return p;
  };
  const S = (p: Page, type: string, x: number, y: number, o: { rot?: number; tag?: string; desc?: string; part?: string; props?: SymProps } = {}) => {
    const s: Sym = { id: newId(), type, x, y, rot: o.rot || 0, tag: o.tag || '', desc: o.desc || '', part: o.part || '', maker: '', props: o.props || {} };
    if (s.part) s.maker = (parts.find((q) => q.pn === s.part) ?? opt.fallback?.find((q) => q.pn === s.part))?.maker ?? '';
    p.syms.push(s);
    return s;
  };
  const W = (p: Page, pts: Pt[], cs = '', col = '') => p.wires.push({ id: newId(), pts, cs, col });
  const choose = (who: string, what: string, type: string, w: PartWant) => {
    // the preferred parts (one maker) first, every other part where they have nothing suitable
    const p = pickPart(parts, type, w) ?? (opt.fallback ? pickPart(opt.fallback, type, w) : undefined);
    if (!p) warnings.push($t('{0}: {1} için uygun parça bulunamadı{2}', who, what, w.rated != null ? ` (≥ ${w.rated} A)` : ''));
    return p?.pn ?? '';
  };
  /** terminal for a conductor cross-section: the smallest rail (or earth) terminal that takes it; none for parallel cables */
  const termPart = (sec: string, pe: boolean) => {
    const c = Number(sec);
    if (!Number.isFinite(c)) return '';
    const sizes = [2.5, 4, 6, 10, 16, 35, 70, 120, 240];
    const fit = sizes.find((x) => x >= c);
    if (fit == null) return '';
    if (pe) return has(fit <= 4 ? 'KL-4-PE' : `KL-${fit}-PE`) || has(`KL-${fit}`);
    return has(fit === 2.5 ? 'KL-2.5' : `KL-${fit}`);
  };
  const wireCs = (a: number) => (feederSection(a).startsWith('2×') ? '' : feederSection(a));

  /* ---- incomer: incoming supply from the left, main switch, outgoing busbars to the right ---- */
  const p = page($t('Giriş · {0} {1} A', inc.ats && inc.manual ? $t('Komütatör şalter I-0-II') : $t(INCOMER_NAMES[inc.kind]), inc.amps));
  const cs = wireCs(inc.amps);
  const a = X(2);
  // incoming conductors: [name, y in, column offset, y of the outgoing busbar, type, colour]
  const INC: [string, number, number, number, PType, string][] = [
    ['L1', 24, 0, 160, 'L', 'BK'], ['L2', 32, 8, 176, 'L', 'BK'], ['L3', 40, 16, 192, 'L', 'BK'], ['N', 48, 24, 200, 'N', 'BU'], ['PE', 56, 32, 208, 'PE', 'GNYE'],
  ];
  S(p, 'text', 34, 18, { props: { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 } });
  const Q = tag('Q');
  // transfer by hand: the change-over switch is the main switch, grid on I, generator on II (16 mm per pole,
  // drawn left of N and PE so its poles stay clear of their conductors)
  const manual = !!inc.ats && !!inc.manual, xc = a - 16;
  if (manual) S(p, 'cos3', xc, 96, { tag: Q, desc: $t('Şebeke / jeneratör komütatör şalteri'), part: choose(Q, $t('komütatör şalter'), 'cos3', { rated: inc.amps }) });
  else S(p, INCOMER_TYPE[inc.kind], a, 64, { tag: Q, desc: $t('Ana şalter'), part: choose(Q, $t('ana şalter'), INCOMER_TYPE[inc.kind], { rated: inc.amps }) });
  const ctPart = inc.meter ? choose(Q, $t('akım trafosu'), 'ct', { rated: inc.amps }) : '';
  // transfer to a generator: grid contactor KM1 below the main switch, generator contactor KM2 on its own page
  const ats = !!inc.ats && !manual;
  // with air circuit breakers the two motorised breakers transfer themselves (closing and shunt trip coils),
  // electrically interlocked by each other's auxiliary contacts: no contactors at these currents
  const acbAts = ats && inc.kind === 'acb';
  const Q2 = ats || manual ? tag('Q') : '';
  const KM1 = ats ? (acbAts ? Q : tag('K')) : '', KM2 = ats ? (acbAts ? Q2 : tag('K')) : '', KA = ats ? tag('K') : '';
  const kmPart = ats && !acbAts ? choose(KM1, $t('kontaktör'), 'coil', { rated: inc.amps, text: '230 V AC', nc: 1 }) : '';
  const yBelow = (ats && !acbAts) || manual ? 120 : 88;
  if (manual)
    // the generator's phases come in from the left below the grid's, to the II side of the switch
    [72, 80, 88].forEach((y, i) => {
      S(p, 'brk', 28, y, { rot: 1, props: { name: `GL${i + 1}`, ptype: 'L' } });
      W(p, [[28, y], [xc + 4 + 16 * i, y], [xc + 4 + 16 * i, 96]], cs, 'BK');
    });
  if (ats) {
    if (!acbAts) S(p, 'k3', a, 100, { tag: KM1 });
    // control voltage of the grid side, taken between the main switch and the grid contactor
    S(p, 'fuse', a - 24, 100, { tag: tag('F'), desc: $t('Transfer kumanda · şebeke'), part: has('SG-2A-5x20') });
    W(p, [[a, 94], [a - 24, 94], [a - 24, 100]], '1.5', 'BK');
    W(p, [[a - 24, 116], [a - 24, 124]], '1.5', 'BK');
    S(p, 'brk', a - 24, 124, { props: { name: '1LS', ptype: 'L' } });
  }
  const cts: { x: number; y: number }[] = [];
  const steps = inc.comp ?? [];
  const KRPR = steps.length ? tag('K') : '';
  /** the rest of a phase busbar to the right edge; L1 carries the controller's current transformer */
  const tail = (x0: number, y: number, i: number, wcs: string, col: string) => {
    if (i !== 0 || !steps.length) return void W(p, [[x0, y], [400, y]], wcs, col);
    const cx = X(6) + 24;
    W(p, [[x0, y], [cx, y]], wcs, col);
    S(p, 'ct', cx, y, { rot: 3, tag: tag('T'), desc: $t('Kompanzasyon akım trafosu'), part: choose(KRPR, $t('akım trafosu'), 'ct', { rated: inc.amps }) });
    W(p, [[cx + 16, y], [400, y]], wcs, col);
    W(p, [[cx + 4, y - 8], [cx + 4, 128]], '2.5', 'BK');
    W(p, [[cx + 12, y - 8], [cx + 12, 128]], '2.5', 'BK');
    S(p, 'brk', cx + 4, 128, { rot: 2, props: { name: 'KTk', ptype: '' } });
    S(p, 'brk', cx + 12, 128, { rot: 2, props: { name: 'KTl', ptype: '' } });
  };
  // without feeders or capacitor steps (a transfer board) the busbars end in the board's own output
  const onward = feeders.length > 0 || steps.length > 0;
  if (!onward) S(p, 'text', 300, 220, { props: { text: $t('→ Çıkış baraları'), size: 2.5 } });
  INC.forEach(([name, yIn, dx, yOut, pt, col], i) => {
    const x = a + dx;
    S(p, 'pot', 28, yIn, { props: { name, ptype: pt } });
    if (onward || ((ats || manual) && name !== 'PE')) S(p, 'brk', 400, yOut, { rot: 3, props: { name, ptype: pt } });
    if (i < 3 && manual) {
      // grid to the I side, the switch's outputs on to the busbars (through the current transformers)
      const xo = xc + 16 * i;
      W(p, [[28, yIn], [xo - 4, yIn], [xo - 4, 96]], cs, col);
      if (inc.meter) {
        const cx = X(4) + 24 * i;
        W(p, [[xo, 120], [xo, yOut], [cx, yOut]], cs, col);
        S(p, 'ct', cx, yOut, { rot: 3, tag: tag('T'), desc: $t('{0} akım trafosu', name), part: ctPart });
        tail(cx + 16, yOut, i, cs, col);
        cts.push({ x: cx, y: yOut });
      } else {
        W(p, [[xo, 120], [xo, yOut]], cs, col);
        tail(xo, yOut, i, cs, col);
      }
    } else if (i < 3) {
      W(p, [[28, yIn], [x, yIn], [x, 64]], cs, col);
      if (inc.meter) {
        // one current transformer per phase on the outgoing busbar, staggered so their secondaries run up freely
        const cx = X(4) + 24 * i;
        if (ats && !acbAts) W(p, [[x, 88], [x, 100]], cs, col);
        W(p, [[x, yBelow], [x, yOut], [cx, yOut]], cs, col);
        S(p, 'ct', cx, yOut, { rot: 3, tag: tag('T'), desc: $t('{0} akım trafosu', name), part: ctPart });
        tail(cx + 16, yOut, i, cs, col);
        cts.push({ x: cx, y: yOut });
      } else {
        if (ats && !acbAts) W(p, [[x, 88], [x, 100]], cs, col);
        W(p, [[x, yBelow], [x, yOut]], cs, col);
        tail(x, yOut, i, cs, col);
      }
    } else W(p, [[28, yIn], [x, yIn], [x, yOut], [400, yOut]], cs, col);
  });
  if (inc.meter) {
    const ax = X(4), ay = 100;
    S(p, 'emeter', ax, ay, { tag: tag('P'), desc: $t('Enerji analizörü'), part: has('EA-3F') });
    // CT secondaries k/l up to the current inputs
    cts.forEach(({ x, y }) => {
      W(p, [[x + 4, y - 8], [x + 4, ay + 32]], '2.5', 'BK');
      W(p, [[x + 12, y - 8], [x + 12, ay + 32]], '2.5', 'BK');
    });
    // voltage inputs from the busbars, the phases through 2 A fuses
    INC.slice(0, 4).forEach(([name, , , yOut], i) => {
      const x = ax + 68 + 8 * i;
      if (i < 3) {
        W(p, [[x, yOut], [x, 152]], '1', 'BK');
        // no description: three fuses 8 mm apart would print over each other
        S(p, 'fuse', x, 136, { tag: tag('F'), part: has('SG-2A-5x20') });
        W(p, [[x, 136], [x, ay + 32]], '1', 'BK');
      } else W(p, [[x, yOut], [x, ay + 32]], '1', 'BU');
    });
  }
  if (inc.spd) {
    // rotated half a turn: its line terminals face the busbars below, PE runs round to the PE busbar
    const sx = X(8);
    S(p, 'spd4', sx, 136, { rot: 2, tag: tag('F'), desc: $t('Aşırı gerilim koruma'), part: has('PF-T12-4P') || has('PF-T2-4P') });
    INC.slice(0, 4).forEach(([, , , yOut], i) => W(p, [[sx - 8 * i, yOut], [sx - 8 * i, 136]], '10', i === 3 ? 'BU' : 'BK'));
    W(p, [[sx - 12, 112], [sx - 12, 100], [X(9) - 12, 100], [X(9) - 12, 208]], '10', 'GNYE');
  }

  if (manual) {
    /* ---- generator: through its breaker to the II side of the change-over switch ---- */
    const g = page($t('Jeneratör girişi · {0} A', inc.amps));
    const G = tag('G');
    S(g, 'gen3', a, 184, { tag: G, desc: $t('Jeneratör') });
    S(g, INCOMER_TYPE[inc.kind], a, 100, { tag: Q2, desc: $t('Jeneratör şalteri'), part: choose(Q2, $t('jeneratör şalteri'), INCOMER_TYPE[inc.kind], { rated: inc.amps }) });
    INC.slice(0, 3).forEach(([, yBus, dx, , , col], i) => {
      const x = a + dx;
      W(g, [[x, 184], [x, 124]], cs, col);
      W(g, [[x, 100], [x, yBus], [400, yBus]], cs, col);
      S(g, 'brk', 400, yBus, { rot: 3, props: { name: `GL${i + 1}`, ptype: 'L' } });
    });
    W(g, [[a + 24, 184], [a + 24, 48], [400, 48]], cs, 'BU');
    S(g, 'brk', 400, 48, { rot: 3, props: { name: 'N', ptype: 'N' } });
    warnings.push($t('Elle transfer bir şablondur: komütatör şalterin nötrü de anahtarlaması (4 kutup) gerekip gerekmediğini topraklama sistemine göre kontrol edin; şebeke ile jeneratör arasında geçiş yalnızca 0 konumundan yapılır.'));
  }
  if (ats) {
    /* ---- generator: from the bottom up through its breaker and contactor KM2 onto the busbar ---- */
    const g = page($t('Jeneratör girişi · {0} A', inc.amps));
    const G = tag('G');
    S(g, 'gen3', a, 184, { tag: G, desc: $t('Jeneratör') });
    S(g, INCOMER_TYPE[inc.kind], a, 100, { tag: Q2, desc: $t('Jeneratör şalteri'), part: choose(Q2, $t('jeneratör şalteri'), INCOMER_TYPE[inc.kind], { rated: inc.amps }) });
    if (!acbAts) S(g, 'k3', a, 64, { tag: KM2 });
    INC.slice(0, 3).forEach(([name, yBus, dx, , pt, col]) => {
      const x = a + dx;
      W(g, [[x, 184], [x, 124]], cs, col);
      if (acbAts) W(g, [[x, 100], [x, yBus], [400, yBus]], cs, col);
      else {
        W(g, [[x, 100], [x, 84]], cs, col);
        W(g, [[x, 64], [x, yBus], [400, yBus]], cs, col);
      }
      S(g, 'brk', 400, yBus, { rot: 3, props: { name, ptype: pt } });
    });
    W(g, [[a + 24, 184], [a + 24, 48], [400, 48]], cs, 'BU');
    S(g, 'brk', 400, 48, { rot: 3, props: { name: 'N', ptype: 'N' } });
    S(g, 'fuse', a - 24, 100, { tag: tag('F'), desc: $t('Transfer kumanda · jeneratör'), part: has('SG-2A-5x20') });
    W(g, [[a, 92], [a - 24, 92], [a - 24, 100]], '1.5', 'BK');
    W(g, [[a - 24, 116], [a - 24, 124]], '1.5', 'BK');
    S(g, 'brk', a - 24, 124, { props: { name: '1LG', ptype: 'L' } });

    /* ---- transfer control 230 V: grid monitor KA, KM1 and KM2 (or the two breakers) interlocked, generator start ---- */
    const c = page($t('Transfer kumanda 230 V · şebeke / jeneratör'));
    if (acbAts) {
      S(c, 'brk', 32, 24, { rot: 1, props: { name: '1LS', ptype: 'L' } });
      W(c, [[32, 24], [160, 24]], '1.5', 'RD');
      S(c, 'brk', 32, 32, { rot: 1, props: { name: '1LG', ptype: 'L' } });
      W(c, [[32, 32], [240, 32]], '1.5', 'RD');
      S(c, 'brk', 32, 224, { rot: 1, props: { name: 'N', ptype: 'N' } });
      W(c, [[32, 224], [312, 224]], '1.5', 'BU');
      W(c, [[72, 24], [72, 100]]);
      S(c, 'coil', 72, 100, { tag: KA, desc: $t('Şebeke var (faz kontrol)'), part: has('YR-4NO4NC-230AC') });
      W(c, [[72, 120], [72, 224]]);
      /** one rung: [bus y, contacts top down, coil type, breaker, description] */
      const rung = (x: number, busY: number, contacts: [string, string][], coil: 'coil_cl' | 'coil_st', q: string, desc: string) => {
        let y = 40;
        W(c, [[x, busY], [x, y]]);
        for (const [type, t] of contacts) {
          S(c, type, x, y, { tag: t });
          W(c, [[x, y + 16], [x, y + 24]]);
          y += 24;
        }
        W(c, [[x, y - 8], [x, 100]]);
        S(c, coil, x, 100, { tag: q, desc });
        W(c, [[x, 120], [x, 224]]);
      };
      // grid breaker closes when the grid is there and the generator breaker is open; the generator side mirrors it
      rung(112, 24, [['no', KA], ['nc', Q2]], 'coil_cl', Q, $t('{0} kapama', Q));
      rung(152, 24, [['no', KA]], 'coil_st', Q2, $t('{0} açma', Q2));
      rung(192, 32, [['nc', KA], ['nc', Q]], 'coil_cl', Q2, $t('{0} kapama', Q2));
      rung(232, 32, [['nc', KA]], 'coil_st', Q, $t('{0} açma', Q));
      term++;
      S(c, 'term', 272, 44, { tag: 'X1', part: has('KL-2.5'), props: { pinNo: String(term) } });
      W(c, [[272, 52], [272, 64]]);
      S(c, 'nc', 272, 64, { tag: KA });
      W(c, [[272, 80], [272, 92]]);
      term++;
      S(c, 'term', 272, 92, { tag: 'X1', part: has('KL-2.5'), props: { pinNo: String(term) } });
      W(c, [[272, 44], [272, 38]]);
      W(c, [[272, 100], [272, 108]]);
      S(c, 'text', 278, 74, { props: { text: $t('→ Jeneratör çalıştır'), size: 2.5 } });
      warnings.push($t('Motorlu şalterli transfer bir şablondur: iki şalterin mekanik kilidini, kurma motorlarının beslemesini, açma/kapama bobinlerinin gerilimini ve geçiş gecikmelerini üretici kılavuzuna göre kontrol edin; açma bobinleri şebeke kesildiğinde de beslenmelidir (UPS ya da akü).'));
    }
    if (!acbAts) {
    S(c, 'brk', 32, 24, { rot: 1, props: { name: '1LS', ptype: 'L' } });
    W(c, [[32, 24], [120, 24]], '1.5', 'RD');
    S(c, 'brk', 32, 32, { rot: 1, props: { name: '1LG', ptype: 'L' } });
    W(c, [[32, 32], [160, 32]], '1.5', 'RD');
    S(c, 'brk', 32, 224, { rot: 1, props: { name: 'N', ptype: 'N' } });
    W(c, [[32, 224], [240, 224]], '1.5', 'BU');
    // KA: grid present (in practice a phase monitoring relay)
    W(c, [[72, 24], [72, 100]]);
    S(c, 'coil', 72, 100, { tag: KA, desc: $t('Şebeke var (faz kontrol)'), part: has('YR-4NO4NC-230AC') });
    W(c, [[72, 120], [72, 224]]);
    // KM1 grid contactor: grid present and KM2 off
    W(c, [[112, 24], [112, 40]]);
    S(c, 'no', 112, 40, { tag: KA });
    W(c, [[112, 56], [112, 64]]);
    S(c, 'nc', 112, 64, { tag: KM2 });
    W(c, [[112, 80], [112, 100]]);
    S(c, 'coil', 112, 100, { tag: KM1, desc: $t('Şebeke kontaktörü'), part: kmPart });
    W(c, [[112, 120], [112, 224]]);
    // KM2 generator contactor: grid absent and KM1 off
    W(c, [[152, 32], [152, 40]]);
    S(c, 'nc', 152, 40, { tag: KA });
    W(c, [[152, 56], [152, 64]]);
    S(c, 'nc', 152, 64, { tag: KM1 });
    W(c, [[152, 80], [152, 100]]);
    S(c, 'coil', 152, 100, { tag: KM2, desc: $t('Jeneratör kontaktörü'), part: kmPart });
    W(c, [[152, 120], [152, 224]]);
    // generator start: a dry contact to the generator controller over two terminals
    term++;
    S(c, 'term', 192, 44, { tag: 'X1', part: has('KL-2.5'), props: { pinNo: String(term) } });
    W(c, [[192, 52], [192, 64]]);
    S(c, 'nc', 192, 64, { tag: KA });
    W(c, [[192, 80], [192, 92]]);
    term++;
    S(c, 'term', 192, 92, { tag: 'X1', part: has('KL-2.5'), props: { pinNo: String(term) } });
    W(c, [[192, 44], [192, 38]]);
    W(c, [[192, 100], [192, 108]]);
    S(c, 'text', 198, 74, { props: { text: $t('→ Jeneratör çalıştır'), size: 2.5 } });
    warnings.push($t('Şebeke–jeneratör transferi bir şablondur: mekanik kilitli kontaktör ya da motorlu şalter çifti kullanın, geçiş gecikmelerini ve nötrün de anahtarlanması (4 kutup) gerekip gerekmediğini kontrol edin.'));
    }
  }

  if (steps.length) {
    /* ---- power factor correction: breaker, capacitor contactor and capacitor per step ---- */
    const BUSC: [string, number, PType, string][] = [['L1', 24, 'L', 'BK'], ['L2', 32, 'L', 'BK'], ['L3', 40, 'L', 'BK'], ['N', 48, 'N', 'BU'], ['PE', 56, 'PE', 'GNYE']];
    const total = steps.reduce((s, k) => s + k, 0);
    const cp = page($t('Kompanzasyon · {0} kvar', String(total).replace('.', ',')));
    BUSC.forEach(([n, y, t, c]) => {
      S(cp, 'brk', 28, y, { rot: 1, props: { name: n, ptype: t } });
      W(cp, [[28, y], [400, y]], '', c);
      S(cp, 'brk', 400, y, { rot: 3, props: { name: n, ptype: t } });
    });
    const km: string[] = [];
    steps.forEach((k, i) => {
      // the step's conductors sized for its breaker (the next standard rating above 1.43 × the capacitor current)
      const x = X(1) + 36 * i, I = capCurrent(k);
      const F = tag('F'), K = tag('K'), C = tag('C');
      const st = I * 1.43 > 63 ? 'mccb3' : 'cb3n';
      const stPart = choose(F, $t('kademe sigortası'), st, { rated: Math.ceil(I * 1.43) });
      const scs = wireCs(ratedOf([...parts, ...(opt.fallback ?? [])].find((q) => q.pn === stPart)?.d ?? '') ?? I * 1.43);
      km.push(K);
      [0, 8, 16].forEach((dx, j) => W(cp, [[x + dx, 24 + 8 * j], [x + dx, 64]], scs, 'BK'));
      // steps above 63 A (from about 30 kvar) on a moulded case breaker: no miniature breaker that big
      S(cp, st, x, 64, { tag: F, desc: $t('Kademe {0}', i + 1), part: stPart });
      [0, 8, 16].forEach((dx) => W(cp, [[x + dx, 88], [x + dx, 100]], scs, 'BK'));
      S(cp, 'k3', x, 100, { tag: K });
      [0, 8, 16].forEach((dx) => W(cp, [[x + dx, 120], [x + dx, 140]], scs, 'BK'));
      S(cp, 'cap3', x, 140, { tag: C, desc: `${String(k).replace('.', ',')} kvar`, part: has(`KND-${k}`) });
    });

    /* ---- controller: supply, current input and one output per step driving its contactor ---- */
    const cc = page($t('Kompanzasyon kumanda 230 V'));
    S(cc, 'brk', 32, 24, { rot: 1, props: { name: 'L1', ptype: 'L' } });
    W(cc, [[32, 24], [60, 24], [60, 32]], '1.5', 'BK');
    const FK = tag('F');
    S(cc, 'cb1', 60, 32, { tag: FK, desc: $t('Kompanzasyon kumanda'), part: has('OS-C6-1P') });
    S(cc, 'brk', 32, 224, { rot: 1, props: { name: 'N', ptype: 'N' } });
    W(cc, [[32, 224], [396, 224]], '1.5', 'BU');
    const xEnd = X(4) + 24 * (steps.length - 1);
    W(cc, [[60, 56], [60, 64], [xEnd, 64]], '1.5', 'RD');
    S(cc, 'rpr', 72, 100, { tag: KRPR, desc: $t('Reaktif güç rölesi'), part: steps.length <= 6 ? has('RGR-6') : has('RGR-12') });
    W(cc, [[72, 64], [72, 100]], '1.5', 'RD');
    W(cc, [[80, 100], [80, 84], [44, 84], [44, 224]], '1.5', 'BU');
    S(cc, 'brk', 96, 76, { props: { name: 'KTk', ptype: '' } });
    W(cc, [[96, 76], [96, 100]], '2.5', 'BK');
    S(cc, 'brk', 104, 76, { props: { name: 'KTl', ptype: '' } });
    W(cc, [[104, 76], [104, 100]], '2.5', 'BK');
    km.forEach((K, i) => {
      const x = X(4) + 24 * i;
      W(cc, [[x, 64], [x, 80]]);
      S(cc, 'no', x, 80, { tag: KRPR, desc: $t('Kademe {0}', i + 1) });
      W(cc, [[x, 96], [x, 140]]);
      S(cc, 'coil', x, 140, { tag: K, desc: $t('Kademe {0} kontaktörü', i + 1), part: choose(K, $t('kondansatör kontaktörü'), 'coil', { rated: Math.ceil(capCurrent(steps[i]) * 1.43), text: '230 V AC' }) });
      W(cc, [[x, 160], [x, 224]]);
    });
    warnings.push($t('Kompanzasyon bir şablondur: ön şarj dirençli kondansatör kontaktörü kullanın, harmonikli şebekede reaktörlü (detuned) kademe gerekip gerekmediğini ve akım trafosunun yükleri ve kompanzasyonu birlikte gördüğünü kontrol edin.'));
  }

  /* ---- outgoing feeders ---- */
  const BUS: [string, number, PType, string][] = [['L1', 24, 'L', 'BK'], ['L2', 32, 'L', 'BK'], ['L3', 40, 'L', 'BK'], ['N', 48, 'N', 'BU'], ['PE', 56, 'PE', 'GNYE']];
  let fp: Page | null = null, col = COLS + 1, first = '', last = '', single = 0;
  // breaker and RCD tag of every feeder, for the single-line overview
  const slInfo: { t: string; rcd: string; fuse: boolean; kwh: string }[] = [];
  const flush = () => {
    if (fp) fp.desc = $t('Çıkışlar · {0}', first === last ? first : `${first}–${last}`);
  };
  feeders.forEach((f, i) => {
    // a column is 8 mm per conductor: three-phase feeders take two, and no N or PE conductor may reach the
    // busbar ends at x = 400 (a single-phase feeder in the last column would join N and PE there)
    // earth leakage protection above the RCD range: core balance CT, relay and the breaker's shunt trip beside the
    // feeder, one column more
    const toroid = f.rcd && f.poles === 3 && f.amps > MCB_MAX && !f.fuse;
    const w = f.poles === 3 ? (toroid ? 3 : 2) : 1;
    if (!fp || col + w - 1 > COLS || (w === 1 && col === COLS)) {
      flush();
      fp = page('');
      BUS.forEach(([n, y, t, c]) => {
        S(fp!, 'brk', 28, y, { rot: 1, props: { name: n, ptype: t } });
        W(fp!, [[28, y], [400, y]], '', c);
        S(fp!, 'brk', 400, y, { rot: 3, props: { name: n, ptype: t } });
      });
      col = 1;
      first = '';
    }
    const pg = fp, x = X(col), fcs = wireCs(f.amps);
    const fuse = !!f.fuse && f.poles === 3;
    const mcb = f.amps <= MCB_MAX && !fuse, type = f.poles === 1 ? 'cb1' : fuse ? 'fsd3' : mcb ? 'cb3n' : 'mccb3';
    const t = own[i] || tag(mcb ? 'F' : 'Q');
    if (f.fuse && !fuse) warnings.push($t('{0}: tek fazlı çıkışta NH sigortalı yük ayırıcı kullanılmaz; otomatik sigorta seçildi.', t));
    if (!first) first = t;
    last = t;
    // single-phase feeders take L1, L2, L3 in turn to balance the load
    const ph = f.poles === 1 ? BUS[single++ % 3] : null;
    const phases = f.poles === 3 ? [x, x + 8, x + 16] : [x];
    const nX = x + 8 * phases.length, peX = nX + 8;
    if (ph) W(pg, [[x, ph[1]], [x, 64]], fcs, 'BK');
    else phases.forEach((px, k) => W(pg, [[px, 24 + 8 * k], [px, 64]], fcs, 'BK'));
    S(pg, type, x, 64, { tag: t, desc: ph ? `${f.desc} · ${ph[0]}` : f.desc, part: choose(t, fuse ? $t('sigortalı yük ayırıcı') : $t('şalter'), type, { rated: f.amps }) });
    let meter = !!f.meter;
    if (meter && f.amps > METER_MAX) {
      warnings.push($t('{0}: {1} A çıkışta direkt bağlı sayaç kullanılamaz; akım trafolu sayacı elle ekleyin.', t, f.amps));
      meter = false;
    }
    // with a meter the conductors stop above it (y 136) and go on from its outputs (y 156)
    const yEnd = meter ? 136 : 160;
    let rcd = f.rcd;
    if (rcd && f.amps > MCB_MAX) {
      if (!toroid) warnings.push($t('{0}: NH sigortalı çıkışın açtırma bobini olmadığından toroidli kaçak akım koruması çizilmedi; kaçak akım koruması için kesicili çıkış seçin.', t));
      rcd = false;
    }
    let yN = 160, rcdTag = '';
    if (toroid) rcdTag = earthLeakage({ S, W, tag, has, choose, warnings }, pg, x, nX, t, f.amps);
    if (rcd) {
      phases.forEach((px) => W(pg, [[px, 88], [px, 100]], fcs, 'BK'));
      const rType = f.poles === 3 ? 'rcd4' : 'rcd2', rt = tag('F');
      rcdTag = rt;
      S(pg, rType, x, 100, { tag: rt, desc: $t('{0} kaçak akım', t), part: choose(rt, $t('kaçak akım rölesi'), rType, { rated: f.amps, text: f.amps <= 40 ? '30 mA' : '300 mA' }) });
      W(pg, [[nX, 48], [nX, 100]], fcs, 'BU');
      phases.forEach((px) => W(pg, [[px, 124], [px, yEnd]], fcs, 'BK'));
      yN = 124;
    } else phases.forEach((px) => W(pg, [[px, 88], [px, yEnd]], fcs, 'BK'));
    W(pg, rcd ? [[nX, yN], [nX, yEnd]] : [[nX, 48], [nX, yEnd]], fcs, 'BU');
    let kwh = '';
    if (meter) {
      const mType = f.poles === 3 ? 'kwh3' : 'kwh1';
      kwh = tag('P');
      S(pg, mType, x, 136, { tag: kwh, desc: $t('{0} sayacı', f.desc || t), part: choose(kwh, $t('elektrik sayacı'), mType, { rated: f.amps }) });
      for (const px of [...phases, nX]) W(pg, [[px, 156], [px, 160]], fcs, px === nX ? 'BU' : 'BK');
    }
    W(pg, [[peX, 56], [peX, 160]], fcs, 'GNYE');
    // above 160 A (and for parallel cables) the cable lands straight on the breaker, not on rail terminals
    const direct = f.amps > DIRECT_FROM || !termPart(feederSection(f.amps), false);
    for (const tx of [...phases, nX, peX]) {
      if (direct) {
        W(pg, [[tx, 160], [tx, 168]], fcs, tx === peX ? 'GNYE' : tx === nX ? 'BU' : 'BK');
        continue;
      }
      term++;
      S(pg, 'term', tx, 160, { tag: 'X1', part: termPart(feederSection(f.amps), tx === peX), props: { pinNo: String(term) } });
    }
    for (const tx of [...phases, nX, peX]) W(pg, [[tx, 168], [tx, 200]], fcs, tx === peX ? 'GNYE' : tx === nX ? 'BU' : 'BK');
    // the cable 4 mm beyond its outer conductors: room for the next feeder's cable label
    S(pg, 'cable', x - 4, 184, {
      tag: tag('W'),
      props: {
        len: peX - x + 8,
        cableType: 'NYY-J',
        cores: f.poles === 3 ? 'BN,BK,GY,BU,GNYE' : 'BN,BU,GNYE',
        cs: feederSection(f.amps),
        ...(f.cableLen ? { length: String(f.cableLen) } : {}),
      },
    });
    S(pg, 'text', x - 4, 208, { props: { text: `→ ${f.desc || t}`, size: 2.5 } });
    slInfo.push({ t, rcd: rcdTag, fuse, kwh });
    col += w;
  });
  flush();

  /* ---- single-line overview: incomer, busbar, one line per feeder (eight per page) ---- */
  const PER = ats || manual ? 7 : 8;
  for (let from = 0; opt.singleLine !== false && from < Math.max(feeders.length, 1); from += PER) {
    const group = feeders.slice(from, from + PER);
    const sp: Page = { id: newId(), desc: $t('Tek hat şeması · dağıtım{0}', feeders.length > PER ? ` ${from / PER + 1}` : ''), kind: 'single', syms: [], wires: [] };
    pages.push(sp);
    const SL = (type: string, x: number, y: number, t = '', props: SymProps = {}, desc = '') =>
      sp.syms.push({ id: newId(), type, x, y, rot: 0, tag: t, desc, part: '', maker: '', props });
    const WL = (pts: Pt[]) => sp.wires.push({ id: newId(), pts, cs: '', col: '' });
    const x0 = ats || manual ? 128 : 88;
    // a metered transfer: grid and generator join above the busbar, the current transformers in the common line
    const BUS = from === 0 && (ats || manual) && inc.meter ? 96 : 72;
    const xEnd = x0 + 40 * Math.max(group.length - 1, 0) + 16;
    if (from === 0) {
      SL('text', 34, 18, '', { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 });
      SL('sl_3p', 48, 28);
      if (manual) {
        // the change-over switch: its two sides (one tag), mechanically linked; the generator through its breaker
        WL([[48, 22], [48, 52]]);
        SL('sl_lbs', 48, 52, Q, { amps: String(inc.amps) });
        SL('text', 84, 18, '', { text: $t('Jeneratör'), size: 2.5 });
        WL([[88, 22], [88, 34]]);
        SL('sl_3p', 88, 28);
        SL('sl_cb', 88, 34, Q2, { amps: String(inc.amps), swkind: inc.kind });
        WL([[88, 50], [88, 52]]);
        SL('sl_lbs', 88, 52, Q, { amps: String(inc.amps) });
        SL('mint', 46, 59.5, '', { pts: '0,0 40,0' });
        SL('text', 54, 44, '', { text: `I-0-II ${inc.amps} A`, size: 2 });
        WL([[88, 68], [88, 72]]);
      } else {
        WL([[48, 22], [48, 34]]);
        SL('sl_cb', 48, 34, Q, { amps: String(inc.amps), swkind: inc.kind });
        SL('text', 54, 44, '', { text: `${inc.amps} A`, size: 2 });
      }
      if (ats) {
        // KM1 under the main switch; the generator comes in at x = 88 through Q2 and KM2 (breakers alone with ACBs)
        WL([[48, 50], [48, 52]]);
        if (acbAts) WL([[48, 52], [48, 68]]);
        else SL('sl_k', 48, 52, KM1);
        SL('text', 84, 18, '', { text: $t('Jeneratör'), size: 2.5 });
        WL([[88, 22], [88, 34]]);
        SL('sl_3p', 88, 28);
        SL('sl_cb', 88, 34, Q2, { amps: String(inc.amps), swkind: inc.kind });
        WL([[88, 50], [88, 52]]);
        if (acbAts) WL([[88, 52], [88, 68]]);
        else SL('sl_k', 88, 52, KM2);
        WL([[88, 68], [88, 72]]);
      }
      const pm = p.syms.find((s) => s.type === 'emeter')?.tag ?? '', fspd = p.syms.find((s) => s.type === 'spd4')?.tag ?? '';
      if (ats || manual) {
        WL([[48, 68], [48, 72]]);
        if (inc.meter) {
          WL([[48, 72], [88, 72]]);
          WL([[68, 72], [68, 74]]);
          SL('sl_ct', 68, 74);
          SL('text', 60.5, 83, '', { text: '3×', size: 2 });
          SL('sl_meter', 80, 82);
          SL('text', 95.5, 80.6, '', { text: pm ? '-' + pm : '', size: 2.2 });
          WL([[68, 90], [68, BUS]]);
        }
      } else if (inc.meter) {
        // three current transformers under the main switch, their secondaries to the energy analyser
        WL([[48, 50], [48, 52]]);
        SL('sl_ct', 48, 52);
        SL('text', 40.5, 61, '', { text: '3×', size: 2 });
        SL('sl_meter', 60, 60);
        SL('text', 75.5, 58.6, '', { text: pm ? '-' + pm : '', size: 2.2 });
        WL([[48, 68], [48, 72]]);
      } else WL([[48, 50], [48, 72]]);
      WL([[inc.spd ? 34 : BUS === 72 ? 48 : 68, BUS], [Math.max(xEnd, 120), BUS]]);
      // surge protective device: from the busbar to earth
      if (inc.spd) SL('sl_spd', 36, BUS, fspd);
    } else WL([[40, BUS], [Math.max(xEnd, 120), BUS]]);
    group.forEach((f, i) => {
      const x = x0 + 40 * i, tg = slInfo[from + i];
      let y = BUS;
      WL([[x, y], [x, y + 12]]);
      if (f.poles === 3) SL('sl_3p', x, y + 6);
      // the values ride on the symbols, so this page can be edited and turned into a board again
      SL(tg?.fuse ? 'sl_fsd' : 'sl_cb', x, y + 12, tg?.t ?? '', { amps: String(f.amps), poles: f.poles === 1 ? '1' : '3', ...(f.cableLen ? { length: String(f.cableLen) } : {}) }, f.desc);
      y += tg?.fuse ? 36 : 28;
      if (tg?.rcd) {
        WL([[x, y], [x, y + 4]]);
        SL('sl_rcd', x, y + 4, tg.rcd, { amps: String(f.amps) });
        y += 20;
      }
      if (tg?.kwh) {
        WL([[x, y], [x, y + 4]]);
        SL('sl_kwh', x, y + 4, tg.kwh);
        y += 24;
      }
      WL([[x, y], [x, y + 36]]);
      const lines = [`${tg?.fuse ? 'gG ' : ''}${f.amps} A${f.poles === 1 ? ' 1P' : ''}`, `NYY-J ${f.poles === 3 ? 5 : 3}×${feederSection(f.amps)}`, f.cableLen ? `${f.cableLen} m` : '', (f.desc || '').slice(0, 16)].filter(Boolean);
      lines.forEach((t, j) => SL('text', x + 2, y + 6 + 5 * j, '', { text: t, size: 2 }));
    });
    if (steps.length && from + PER >= feeders.length && group.length < PER) {
      const x = x0 + 40 * group.length;
      const d = BUS - 72;
      WL([[xEnd - 16, BUS], [x, BUS]]);
      WL([[x, BUS], [x, 84 + d]]);
      SL('sl_3p', x, 78 + d);
      SL('sl_cb', x, 84 + d);
      WL([[x, 100 + d], [x, 108 + d]]);
      [$t('Kompanzasyon'), `${steps.length} × … ${String(steps.reduce((s, k) => s + k, 0)).replace('.', ',')} kvar`].forEach((t, j) => SL('text', x + 2, 114 + d + 5 * j, '', { text: t, size: 2 }));
    }
  }

  if (inc.amps >= BUSBAR_FROM) {
    // the main circuit (incomer, generator, main busbars) is copper busbar at these currents
    const ph = busbarSize(inc.amps), pe = busbarSize(inc.amps / 2);
    const mains = new Set([p.id, pages.find((q) => q.desc.startsWith($t('Jeneratör girişi · {0} A', inc.amps)))?.id]);
    for (const pg of pages) {
      if (pg.kind === 'single') continue;
      for (const w of pg.wires) {
        const isMain = mains.has(pg.id) && w.cs === '' && ['BK', 'BU', 'GNYE'].includes(w.col);
        const isBus = w.pts.length === 2 && w.pts[0][0] === 28 && w.pts[1][0] === 400 && w.pts[0][1] === w.pts[1][1] && w.pts[0][1] <= 56;
        if (isMain || isBus) w.bus = w.col === 'GNYE' ? pe : ph;
      }
    }
    warnings.push($t('Ana devre bakır bara ile çizildi: faz ve nötr {0}, PE {1} mm Cu (boyalı, 35 °C). Bara mesnetlerini, kısa devre dayanımını ve bağlantı baralarını üreticinin sistemine göre kontrol edin.', ph, pe));
  }
  if (feeders.length)
    warnings.push($t('Kablo kesitleri döşeme yöntemi C ve 30 °C için şalter akımına göre seçildi; gerilim düşümünü, kısa devre dayanımını ve seçiciliği kontrol edin.'));
  return { pages, warnings };
}
